import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Link, Typography, Skeleton } from "@mui/material";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import dayjs from "dayjs";
import { ShiftData, SetShiftCards } from "../../../providerSupportingComponents/ProviderAvailability/setShiftCards/SetShiftCards";
import { ShiftFilterSelector, FilterConfig } from "../../../providerSupportingComponents/ProviderAvailability/ShiftFilterSelector";
import RequestPopOver from "components/providerSupportingComponents/requestPopover/RequestPopOver";
import CurrentAdditionalAvailability, { AdditionalAvailabilityItem } from "components/providerSupportingComponents/ProviderAvailability/CurrentAdditionalAvailability";
import SendRequestDialog from "components/providerSupportingComponents/ProviderAvailability/SendRequestDialog";
import { useSubmitApi } from "hooks/customHooks/authHook";
import { ApiCalls } from "../AllAppointmentsApi";
import { ProviderAvailabilityDaysOfWeeksetShift, ProviderAvailabilityDaysOfWeekAdditionalAvailability, ProviderAvailabilityBlocksOfDaysetShift, ProviderAvailabilityBlocksOfDayAdditionalAvailability, BreadcrumbTexts, providerAvailabilityDialogModes } from "constant/BiPortalConstants";

interface ScheduleItem {
  isAdditionalShift: number;
  shiftId: number;
  shiftName: string;
  availableDays: number[];
  startTime: string;
  endTime: string;
  daysOfWeek: string;
}

interface AvailabilityServiceProps {
  requestType?: string;
  currentSchedule?: ScheduleItem[];
  initialAdditionalAvailability?: AdditionalAvailabilityItem[];
  providerId: number | null;
  onShiftDataError?: (error: string) => void;
  providerInfo?: {
    providerId: number;
    providerEmail: string;
    providerName: string;
    providerClinicName: string;
  };
  onSuccess?: () => void;
  isLoadingSchedule?: boolean;
}

export const AvailabilityService: React.FC<AvailabilityServiceProps> = ({ 
  requestType = BreadcrumbTexts.setShift, 
  currentSchedule,
  initialAdditionalAvailability = [],
  providerId,
  onShiftDataError,
  providerInfo,
  onSuccess,
  isLoadingSchedule = false,
}) => {
  const { getApiCall, submitApiCall } = useSubmitApi();
  const hasFetchedShiftsRef = useRef<boolean>(false);
  
  const [shiftsData, setShiftsData] = useState<ShiftData[]>([]);
  const [isLoadingShifts, setIsLoadingShifts] = useState<boolean>(true);

  const isScheduleLoading = isLoadingSchedule || (!currentSchedule && providerId);

  const currentSetShift = React.useMemo(() => {
    if (!currentSchedule) return undefined;
    const setShift = currentSchedule.find(item => item.isAdditionalShift === 0);
    if (setShift) {
      return {
        days: setShift.availableDays,
        startTime: setShift.startTime,
        endTime: setShift.endTime,
      };
    }
    return undefined;
  }, [currentSchedule]);

  useEffect(() => {
    if (hasFetchedShiftsRef.current || !providerId) return;
    
    hasFetchedShiftsRef.current = true;
    
    const fetchShiftAvailability = async () => {
      try {
        const ApiCall = requestType === BreadcrumbTexts.setShift ? ApiCalls.SetShiftAvailabilityCall : ApiCalls.AdditionalAvailabilityCall;
        setIsLoadingShifts(true);
        const response = await getApiCall(ApiCall, { 
          providerId 
        });
        
        if (response?.data && Array.isArray(response.data)) {
          setShiftsData(response.data);
        } else {
          setShiftsData([]);
        }
      } catch (error) {
        console.error(error, "[AvailabilityService] shift availability error");
        onShiftDataError?.("Unable to fetch shift availability data. Please try again.");
        setShiftsData([]);
      } finally {
        setIsLoadingShifts(false);
      }
    };

    fetchShiftAvailability();
  }, [providerId, getApiCall, onShiftDataError, requestType]);

  const [selectedDays, setSelectedDays] = useState<(string | number[])[]>([]);
  const [selectedBlocks, setSelectedBlocks] = useState<(string | number[])[]>([]);
  const [selectedShifts, setSelectedShifts] = useState<number[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [dialogMode, setDialogMode] = useState<string>(providerAvailabilityDialogModes.addAvailability);
  const [removalItem, setRemovalItem] = useState<AdditionalAvailabilityItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const filterConfigs: FilterConfig[] = useMemo(() => [
    {
      label: "Days",
      placeholder: "Select days",
      data: requestType === BreadcrumbTexts.setShift ? ProviderAvailabilityDaysOfWeeksetShift : ProviderAvailabilityDaysOfWeekAdditionalAvailability,
    },
    {
      label: "Blocks",
      placeholder: "Select blocks",
      data: requestType === BreadcrumbTexts.setShift ? ProviderAvailabilityBlocksOfDaysetShift : ProviderAvailabilityBlocksOfDayAdditionalAvailability,
    }
  ], []);

  const selectedFilterValues = [selectedDays, selectedBlocks];

  const handleFilterChange = (filterIndex: number, values: (string | number[])[]) => {
    if (filterIndex === 0) {
      setSelectedDays(values);
    } else if (filterIndex === 1) {
      setSelectedBlocks(values);
    }
  };

  const handleRemoveAvailability = (id: number) => {
    const itemToRemove = initialAdditionalAvailability.find(item => item.id === id);
    if (itemToRemove) {
      setRemovalItem(itemToRemove);
      setDialogMode(providerAvailabilityDialogModes.removeAvailability);
      setIsDialogOpen(true);
    }
  };

  const handleCardSelect = (shiftId: number) => {
    
    if (requestType === BreadcrumbTexts.setShift) {
      setSelectedShifts([shiftId]);
    } else {
      setSelectedShifts(prevSelected => {
        const isAlreadySelected = prevSelected.includes(shiftId);
        
        if (isAlreadySelected) {
          const newSelection = prevSelected.filter(id => id !== shiftId);
          return newSelection;
        } else {
          const newSelection = [...prevSelected, shiftId];
          return newSelection;
        }
      });
    }
  };

  const handleCancel = () => {
    setSelectedShifts([]);
  };

  const handleStartRequest = () => {
    setDialogMode(providerAvailabilityDialogModes.addAvailability);
    setSubmitError(null);
    setIsDialogOpen(true);
  };

  const handleSendRequest = async (data: {
    startDate: string;
    isSchoolScheduleChange: string;
    additionalNotes: string;
  }) => {
    setIsSubmitting(true);
    setSubmitError(null);
    console.log(dialogMode, "[AvailabilityService] dialogMode");

    const formatDateToMidnightUTC = (dateString: string): string => {
      if (!dateString) return "";
      const date = dayjs(dateString, "MM/DD/YYYY");
      return date.format("YYYY-MM-DD") + "T00:00:00.000Z";
    };

    if (dialogMode === providerAvailabilityDialogModes.addAvailability) {
      const selectedShiftData = shiftsData.filter(shift => {
        const cardId = shift.shiftId || shift.blockId;
        return cardId !== undefined && selectedShifts.includes(cardId);
      });
      
      let payload: any;

      if (requestType === BreadcrumbTexts.additionalAvailability && providerInfo) {
        const requestedShift = selectedShiftData.map(shift => ({
          blockId: shift.blockId || 0,
          days: shift.availableDays,
          startTime: shift.startTime,
          endTime: shift.endTime,
        }));

        payload = {
          isAdditionalShift: true,
          isRemovalRequest: false,
          providerId: providerInfo.providerId,
          providerEmail: providerInfo.providerEmail,
          providerName: providerInfo.providerName,
          providerClinic: providerInfo.providerClinicName,
          providerCurrentSetShift: {
            days: currentSetShift?.days || [],
            startTime: currentSetShift?.startTime || "",
            endTime: currentSetShift?.endTime || "",
          },
          shiftInformation: {
            requestedShift,
            newRequestStartDate: formatDateToMidnightUTC(data.startDate),
            isSchoolScheduleChange: data.isSchoolScheduleChange === "yes",
            additionalNotes: data.additionalNotes || "",
          },
        };

      } else if (requestType === BreadcrumbTexts.setShift && providerInfo) {
        const requestedShift = selectedShiftData.map(shift => ({
          shiftId: shift.shiftId || 0,
          days: shift.availableDays,
          startTime: shift.startTime,
          endTime: shift.endTime,
        }));

        payload = {
          isAdditionalShift: false,
          isRemovalRequest: false,
          providerId: providerInfo.providerId,
          providerEmail: providerInfo.providerEmail,
          providerName: providerInfo.providerName,
          providerClinic: providerInfo.providerClinicName,
          providerCurrentSetShift: {
            days: currentSetShift?.days || [],
            startTime: currentSetShift?.startTime || "",
            endTime: currentSetShift?.endTime || "",
          },
          shiftInformation: {
            requestedShift,
            newRequestStartDate: formatDateToMidnightUTC(data.startDate),
            isSchoolScheduleChange: data.isSchoolScheduleChange === "yes",
            additionalNotes: data.additionalNotes || "",
          },
        };

      }

      try {
        const response = await submitApiCall(
          ApiCalls.SaveShiftChangeRequestCall,
          payload
        );
        console.log(response, "[AvailabilityService] Save Shift Response");
        
        setIsDialogOpen(false);
        setSelectedShifts([]);
        setIsSubmitting(false);
        
        if (onSuccess) {
          onSuccess();
        }
        
      } catch (error) {
        console.log(error, "[AvailabilityService] Save Shift Response Error");
        setSubmitError("There has been an error while trying to send your request. Please try again.");
        setIsSubmitting(false);
      }
    } else if (dialogMode === providerAvailabilityDialogModes.removeAvailability && removalItem && providerInfo) {
      const payload = {
        isAdditionalShift: true,
        isRemovalRequest: true,
        providerId: providerInfo.providerId,
        providerEmail: providerInfo.providerEmail,
        providerName: providerInfo.providerName,
        providerClinic: providerInfo.providerClinicName,
        providerCurrentSetShift: {
          days: currentSetShift?.days || [],
          startTime: currentSetShift?.startTime || "",
          endTime: currentSetShift?.endTime || "",
        },
        shiftInformation: {
          requestedShift: [{
            shiftId: removalItem.id,
            days: removalItem.days,
            startTime: removalItem.startTime,
            endTime: removalItem.endTime,
          }],
          newRequestStartDate: formatDateToMidnightUTC(data.startDate),
          isSchoolScheduleChange: false,
          additionalNotes: data.additionalNotes || "",
        },
      };

      try {
        const response = await submitApiCall(
          ApiCalls.SaveShiftChangeRequestCall,
          payload
        );
        console.log(response, "[AvailabilityService] Removal Response");
        
        setIsDialogOpen(false);
        setRemovalItem(null);
        setIsSubmitting(false);
        
        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        console.log(error, "[AvailabilityService] Removal Response Error");
        setSubmitError("There has been an error while trying to send your request. Please try again.");
        setIsSubmitting(false);
      }
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setRemovalItem(null);
  };

  return (
    <>
    <Box>
        <Typography
        variant="h4"
        sx={{
          fontWeight: 400,
          mb: { xs: 2, md: 3 },
          fontSize: { xs: "20px", sm: "24px", md: "28px" },
        }}
      >
        Clients Waiting for Services
      </Typography>
      <Typography
        variant="body1"
        sx={{
          mb: { xs: 2, md: 3 },
          color: "#424242",
          fontSize: { xs: "14px", sm: "16px" },
        }}
      >
        Select blocks you would prefer to be schedule for <Box component="span" sx={{ fontWeight: 600 }}>{requestType === BreadcrumbTexts.setShift ? BreadcrumbTexts.setShift : BreadcrumbTexts.additionalAvailability}</Box>. Your current shift is shown below.
      </Typography>
      <Typography
        variant="body1"
        sx={{
          mb: { xs: 2, md: 3 },
          color: "#424242",
          fontSize: { xs: "14px", sm: "16px" },
        }}
      >
        Requesting a change in your Additional Availability means that you would like to work during different times and days. 
      </Typography>
      <Typography
        variant="body1"
        sx={{
          mb: { xs: 2, md: 3 },
          color: "#424242",
          fontSize: { xs: "14px", sm: "16px" },
        }}
      >
        If you wish to request a full-time shift, please visit our <Link href="https://app2.greenhouse.io/" target="_blank" rel="noopener noreferrer" sx={{ color: "#10389A", textDecoration: "underline", textDecorationColor: "#10389A"}}>Internal Job Board</Link> first, then contact your ABA Site Manager directly. Full-time options may be limited.
      </Typography>
      <Box
        sx={{
          padding: 2,
          mb: { xs: 2, md: 3 },
          color: "#424242",
          fontSize: { xs: "14px", sm: "16px" },
          border: "1px solid #10389A",
          borderRadius: "5px",
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 20, color: "#1212128F", marginRight: 1 }} />
        <Typography
          variant="body1"
          sx={{
            fontSize: { xs: "14px", sm: "16px" },
            width: "100%",
          }}
        >
          {isScheduleLoading ? (
            <Box component="ul" sx={{ pl: { xs: 2, sm: 3 }, mb: 0, mt: 0, listStyle: "none" }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#000000",
                      fontSize: { xs: "13px", sm: "14px" },
                      whiteSpace: "nowrap",
                    }}
                  >
                    Current set shift:
                  </Typography>
                  <Skeleton variant="text" width="60%" height={20} />
                </Box>
              </Box>
              <Box component="li" sx={{ mb: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#000000",
                      fontSize: { xs: "13px", sm: "14px" },
                      whiteSpace: "nowrap",
                    }}
                  >
                    Additional availability:
                  </Typography>
                  <Skeleton variant="text" width="50%" height={20} />
                </Box>
              </Box>
            </Box>
          ) : currentSchedule && currentSchedule.length > 0 ? (
            <>
              {(() => {
                const setShifts = currentSchedule.filter(item => item.isAdditionalShift === 0);
                const additionalShifts = currentSchedule.filter(item => item.isAdditionalShift === 1);
                
                return (
                  <>
                    <span>
                      Current set shift: {setShifts.length > 0 
                        ? setShifts.map(item => `${item.daysOfWeek} at ${item.startTime} - ${item.endTime}`).join(', ')
                        : 'None'}
                    </span>
                    <br />
                    <span>
                      Additional availability: {additionalShifts.length > 0 
                        ? additionalShifts.map(item => `${item.daysOfWeek} at ${item.startTime} - ${item.endTime}`).join(', ')
                        : 'None'}
                    </span>
                  </>
                );
              })()}
            </>
          ) : (
            <>
              Current set shift: None
              <br />
              Additional availability: None
            </>
          )}
        </Typography>
      </Box>

      {requestType === BreadcrumbTexts.additionalAvailability && (
        <CurrentAdditionalAvailability
          availabilityData={initialAdditionalAvailability}
          onRemove={handleRemoveAvailability}
        />
      )}

      <ShiftFilterSelector
        filters={filterConfigs}
        selectedValues={selectedFilterValues}
        onFilterChange={handleFilterChange}
        isLoading={isLoadingShifts}
      />

      <SetShiftCards 
        shifts={shiftsData} 
        selectedDays={selectedDays}
        selectedBlocks={selectedBlocks}
        selectedShifts={selectedShifts}
        onCardSelect={handleCardSelect}
        isLoading={isLoadingShifts}
      />

      <RequestPopOver
        requestType={requestType}
        selectedShifts={selectedShifts}
        shiftsData={shiftsData}
        onCancel={handleCancel}
        onStartRequest={handleStartRequest}
      />

      {/* Send Request Dialog */}
      <SendRequestDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        requestType={dialogMode === providerAvailabilityDialogModes.removeAvailability ? providerAvailabilityDialogModes.removeAvailability : requestType}
        selectedShifts={
          dialogMode === providerAvailabilityDialogModes.removeAvailability && removalItem
            ? [{
                shiftId: removalItem.id,
                blockId: removalItem.id,
                shiftName: "Removal",
                availableClientCount: 0,
                availableDays: [],
                startTime: "",
                endTime: "",
                blockIds: "",
                daysOfWeek: removalItem.schedule,
                totalClientCount: 0,
              }]
            : shiftsData.filter(shift => {
                const cardId = shift.shiftId || shift.blockId;
                return cardId !== undefined && selectedShifts.includes(cardId);
              })
        }
        onSendRequest={handleSendRequest}
        isRemoval={dialogMode === providerAvailabilityDialogModes.removeAvailability}
        isSubmitting={isSubmitting}
        submitError={submitError}
        onClearError={() => setSubmitError(null)}
      />
    </Box>
    </>
  );
};

export default AvailabilityService;

